/**
 * ConsultationHistoryPage Component
 * Page for viewing completed medical consultation history
 *
 * @version 2025.1.0
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Autocomplete,
  TextField,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { format } from 'date-fns';
import {
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon,
  Description as ProntuarioIcon,
} from '@mui/icons-material';

import { useConsultationHistory } from '../../hooks';
import { Company } from '../../types/company';
import { Professional } from '../../types/professional';
import {
  MedicalOpinion,
  MedicalRecord,
  getMedicalOpinionLabel,
  getMedicalOpinionColor,
} from '../../types/medicalRecord';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import companyService from '../../services/companyService';
import { professionalService } from '../../services/professionalService';
import {
  PageShell,
  DataTable,
  type DataTableColumn,
  InlineFilterBar,
  InlineSelect,
} from '../../components';
import type { InlineSelectOption } from '../../components/common';
import type { ActiveChip } from '../../hooks/useFilterState';
import { formatCPF, formatDate } from '../../utils/employeeFormatters';
import { openSignedFile } from '../../services/fileAccess';

const MEDICAL_OPINION_OPTIONS: InlineSelectOption[] = Object.values(MedicalOpinion).map((op) => ({
  value: op,
  label: getMedicalOpinionLabel(op),
}));

const renderMedicalOpinion = (opinion: MedicalOpinion | null): React.ReactNode => {
  if (!opinion) return <Chip label="-" size="small" />;
  return (
    <Chip
      label={getMedicalOpinionLabel(opinion)}
      size="small"
      color={getMedicalOpinionColor(opinion)}
    />
  );
};

const columns: DataTableColumn<MedicalRecord>[] = [
  {
    id: 'employee',
    label: 'Funcionário',
    primary: true,
    render: (record) => (
      <Typography variant="body2" fontWeight="medium">
        {record.employee?.name || '-'}
      </Typography>
    ),
  },
  {
    id: 'cpf',
    label: 'CPF',
    render: (record) => (
      <Typography variant="body2">
        {record.employee?.cpf ? formatCPF(record.employee.cpf) : '-'}
      </Typography>
    ),
  },
  {
    id: 'company',
    label: 'Empresa',
    render: (record) => (
      <Typography variant="body2">
        {record.employee?.company?.tradeName || record.employee?.company?.legalName || '-'}
      </Typography>
    ),
  },
  {
    id: 'examType',
    label: 'Tipo de Exame',
    render: (record) => (
      <Chip label={record.appointment?.examType || '-'} size="small" variant="outlined" />
    ),
  },
  {
    id: 'date',
    label: 'Data',
    render: (record) => (
      <Typography variant="body2">
        {record.consultationCompletedAt ? formatDate(record.consultationCompletedAt) : '-'}
      </Typography>
    ),
  },
  {
    id: 'professional',
    label: 'Profissional',
    render: (record) => (
      <Typography variant="body2">{record.professional?.name || '-'}</Typography>
    ),
  },
  {
    id: 'medicalOpinion',
    label: 'Resultado',
    render: (record) => renderMedicalOpinion(record.medicalOpinion),
  },
];

export const ConsultationHistoryPage: React.FC = () => {
  // Filter dropdown options

  // Selected filter values
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedProfessional, setSelectedProfessional] = useState<Professional | null>(null);
  const [selectedMedicalOpinion, setSelectedMedicalOpinion] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // PDF generation loading state
  const [generatingPdf, setGeneratingPdf] = useState<{ id: number; type: 'aso' | 'prontuario' } | null>(null);

  // Hook
  const {
    records,
    loading,
    pagination,
    updateFilter,
    clearFilters,
    refresh,
    setPage,
    setLimit,
    generateAso,
    generateProntuario,
  } = useConsultationHistory({
    autoLoad: true,
  });

  // Os dois filtros vinham no mesmo `Promise.all`, então a recusa de um derrubava o outro:
  // quem não lê profissional perdia junto o filtro de empresa, e os dois ficavam vazios sem
  // uma palavra de explicação.
  const carregarEmpresas = useCallback(
    () => companyService.getCompanies({ limit: 200 }).then((res) => res.data ?? []),
    []
  );
  const empresas = useListaPermitida<Company>('company.read', 'Empresas', carregarEmpresas);

  const carregarProfissionais = useCallback(
    () => professionalService.getProfessionals({ limit: 200 }).then((res) => res.data ?? []),
    []
  );
  const profissionais = useListaPermitida<Professional>(
    'professional.read',
    'Profissionais',
    carregarProfissionais
  );

  const companies = empresas.itens;
  const professionals = profissionais.itens;

  // Filter handlers — apply live (no "Aplicar" step); shared by the inline
  // controls' onChange and by the chips' onDelete.
  const handleCompanyChange = useCallback(
    (value: Company | null) => {
      setSelectedCompany(value);
      updateFilter('companyId', value?.id ? Number(value.id) : undefined);
    },
    [updateFilter]
  );

  const handleProfessionalChange = useCallback(
    (value: Professional | null) => {
      setSelectedProfessional(value);
      updateFilter('professionalId', value?.id ? Number(value.id) : undefined);
    },
    [updateFilter]
  );

  const handleMedicalOpinionChange = useCallback(
    (value: string) => {
      setSelectedMedicalOpinion(value);
      updateFilter('medicalOpinion', value ? (value as MedicalOpinion) : undefined);
    },
    [updateFilter]
  );

  const handleStartDateChange = useCallback(
    (date: Date | null) => {
      setStartDate(date);
      updateFilter('startDate', date ? format(date, 'yyyy-MM-dd') : undefined);
    },
    [updateFilter]
  );

  const handleEndDateChange = useCallback(
    (date: Date | null) => {
      setEndDate(date);
      updateFilter('endDate', date ? format(date, 'yyyy-MM-dd') : undefined);
    },
    [updateFilter]
  );

  const handleClearFilters = useCallback(() => {
    setSelectedCompany(null);
    setSelectedProfessional(null);
    setSelectedMedicalOpinion('');
    setStartDate(null);
    setEndDate(null);
    clearFilters();
  }, [clearFilters]);

  // Active filter chips, mirroring the filters currently applied above.
  const chips: ActiveChip[] = [];
  if (selectedCompany) {
    chips.push({
      key: 'companyId',
      label: 'Empresa',
      value: selectedCompany.tradeName || selectedCompany.legalName || '',
      onDelete: () => handleCompanyChange(null),
    });
  }
  if (selectedProfessional) {
    chips.push({
      key: 'professionalId',
      label: 'Profissional',
      value: selectedProfessional.name,
      onDelete: () => handleProfessionalChange(null),
    });
  }
  if (selectedMedicalOpinion) {
    chips.push({
      key: 'medicalOpinion',
      label: 'Resultado',
      value: getMedicalOpinionLabel(selectedMedicalOpinion as MedicalOpinion),
      onDelete: () => handleMedicalOpinionChange(''),
    });
  }
  if (startDate) {
    chips.push({
      key: 'startDate',
      label: 'Data Inicial',
      value: format(startDate, 'dd/MM/yyyy'),
      onDelete: () => handleStartDateChange(null),
    });
  }
  if (endDate) {
    chips.push({
      key: 'endDate',
      label: 'Data Final',
      value: format(endDate, 'dd/MM/yyyy'),
      onDelete: () => handleEndDateChange(null),
    });
  }

  // PDF handlers
  const handleGenerateAso = useCallback(
    async (id: number) => {
      const target = window.open('about:blank', '_blank');
      setGeneratingPdf({ id, type: 'aso' });
      try {
        const result = await generateAso(id);
        if (result?.pdfPath) {
          if (target) {
            try {
              await openSignedFile(result.pdfPath, target);
            } catch (err) {
              target.close();
              console.error('Erro ao abrir ASO:', err);
            }
          }
        } else if (target) {
          target.close();
        }
      } finally {
        setGeneratingPdf(null);
      }
    },
    [generateAso]
  );

  const handleViewAso = useCallback(async (pdfPath: string) => {
    const target = window.open('about:blank', '_blank');
    if (!target) return;
    try {
      await openSignedFile(pdfPath, target);
    } catch (err) {
      target.close();
      console.error('Erro ao abrir ASO:', err);
    }
  }, []);

  const handleGenerateProntuario = useCallback(
    async (id: number) => {
      const target = window.open('about:blank', '_blank');
      setGeneratingPdf({ id, type: 'prontuario' });
      try {
        const result = await generateProntuario(id);
        if (result?.pdfPath) {
          if (target) {
            try {
              await openSignedFile(result.pdfPath, target);
            } catch (err) {
              target.close();
              console.error('Erro ao abrir Prontuário:', err);
            }
          }
        } else if (target) {
          target.close();
        }
      } finally {
        setGeneratingPdf(null);
      }
    },
    [generateProntuario]
  );

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Medicina' },
        { label: 'Histórico de Consultas' },
      ]}
      title="Histórico de Consultas"
      subtitle="Visualize o histórico de consultas médicas concluídas"
      actions={
        <Tooltip title="Atualizar">
          <span>
            <IconButton onClick={refresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      }
    >
        {/* Filters — always visible, apply live */}
        <Box sx={{ mb: 3 }}>
          <InlineFilterBar
            resultCount={pagination.total}
            resultNoun="consultas"
            chips={chips}
            onClearAll={handleClearFilters}
            loading={loading}
          >
            <Autocomplete
              size="small"
              options={[...companies]}
              getOptionLabel={(option) => option.tradeName || option.legalName || ''}
              value={selectedCompany}
              onChange={(_e, value) => handleCompanyChange(value)}
              sx={{ minWidth: 190 }}
              noOptionsText={empresas.bloqueio ?? 'Nenhuma empresa'}
              renderInput={(params) => (
                <TextField {...params} label="Empresa" helperText={empresas.bloqueio} />
              )}
            />
            <Autocomplete
              size="small"
              options={[...professionals]}
              getOptionLabel={(option) => option.name || ''}
              value={selectedProfessional}
              onChange={(_e, value) => handleProfessionalChange(value)}
              sx={{ minWidth: 190 }}
              noOptionsText={profissionais.bloqueio ?? 'Nenhum profissional'}
              renderInput={(params) => (
                <TextField {...params} label="Profissional" helperText={profissionais.bloqueio} />
              )}
            />
            <InlineSelect
              label="Resultado"
              value={selectedMedicalOpinion}
              options={MEDICAL_OPINION_OPTIONS}
              onChange={handleMedicalOpinionChange}
              emptyLabel="Todos"
              minWidth={170}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DatePicker
                label="Data Inicial"
                value={startDate}
                onChange={handleStartDateChange}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { minWidth: 160 },
                  },
                }}
              />
              <DatePicker
                label="Data Final"
                value={endDate}
                onChange={handleEndDateChange}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { minWidth: 160 },
                  },
                }}
              />
            </LocalizationProvider>
          </InlineFilterBar>
        </Box>

      <DataTable
        columns={columns}
        rows={records}
        getRowId={(record) => record.id}
        loading={loading}
        emptyState={{
          title: 'Nenhuma consulta encontrada',
          description:
            'As consultas entram no histórico depois de concluídas no atendimento médico, dentro do período filtrado.',
        }}
        actions={[
          {
            icon: <ProntuarioIcon fontSize="small" />,
            tooltip: 'Gerar Prontuário',
            color: 'info',
            onClick: (record) => {
              if (generatingPdf?.id === record.id) return;
              handleGenerateProntuario(record.id);
            },
          },
          {
            icon: <PdfIcon fontSize="small" />,
            tooltip: 'Baixar ASO',
            color: 'secondary',
            onClick: (record) => handleViewAso(record.asoPdfPath!),
            hidden: (record) => !record.asoPdfPath,
          },
          {
            icon: <PdfIcon fontSize="small" />,
            tooltip: 'Gerar ASO',
            color: 'primary',
            onClick: (record) => {
              if (generatingPdf?.id === record.id) return;
              handleGenerateAso(record.id);
            },
            hidden: (record) => Boolean(record.asoPdfPath),
          },
        ]}
        pagination={{
          page: pagination.page - 1,
          rowsPerPage: pagination.limit,
          totalCount: pagination.total,
          rowsPerPageOptions: [10, 20, 50, 100],
          onPageChange: (newPage) => setPage(newPage + 1),
          onRowsPerPageChange: setLimit,
        }}
      />
    </PageShell>
  );
};

export default ConsultationHistoryPage;
